import { useEffect, useState } from 'react';

/**
 * Component to handle the callback from Microsoft Entra admin consent
 * This page is displayed after consent is provided and instructs the user to close the window
 */
const AdminConsentCallback = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Extract query parameters from URL
    const queryParams = new URLSearchParams(window.location.search);
    const adminConsent = queryParams.get('admin_consent');
    const tenantId = queryParams.get('tenant');
    const error = queryParams.get('error');
    const errorDescription = queryParams.get('error_description');
    
    // Log the consent result for debugging
    console.log('Admin consent callback received:', { adminConsent, tenantId, error, errorDescription });
    
    // Check for errors in the callback
    if (error) {
      setStatus('error');
      setErrorMessage(errorDescription || 'An error occurred during the consent process.');
      return;
    }
    
    // Check if consent was granted
    if (adminConsent === 'True') {
      setStatus('success');
      
      // In a production app, you would make an API call here to update the customer's consent status
    } else {
      setStatus('error');
      setErrorMessage('Admin consent was not granted.');
    }
  }, []);

  // Handle closing the window
  const handleCloseWindow = () => {
    window.close();
  };

  // Content based on status
  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="card p-lg">
            <h2 className="text-primary mb-md">Processing Consent...</h2>
            <p className="mb-md">Please wait while we process your consent.</p>
            <div className="flex justify-center mt-lg">
              <div className="spinner"></div>
            </div>
          </div>
        );
      
      case 'success':
        return (
          <div className="card p-lg">
            <h2 className="text-success mb-md">Admin Consent Provided</h2>
            <p className="mb-md">Thank you for providing admin consent.</p>
            <p className="mb-md">You can now close this window and return to the LMS Manager application.</p>
            <div className="bg-info p-sm mb-md" style={{ borderRadius: 'var(--border-radius-sm)' }}>
              <p className="text-white">
                <strong>Note:</strong> The main application will automatically detect that consent has been provided.
              </p>
            </div>
            <div className="flex justify-center mt-lg">
              <button 
                className="btn btn-primary"
                onClick={handleCloseWindow}
              >
                Close Window
              </button>
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className="card p-lg">
            <h2 className="text-error mb-md">Consent Error</h2>
            <p className="mb-md">{errorMessage || 'An error occurred during the consent process.'}</p>
            <p className="mb-md">Please close this window and try again from the LMS Manager application.</p>
            <div className="flex justify-center mt-lg">
              <button 
                className="btn btn-primary"
                onClick={handleCloseWindow}
              >
                Close Window
              </button>
            </div>
          </div>
        );
    }
  };

  // Simple layout for popup window
  return (
    <div className="page-container" style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h1 className="page-title text-center mb-lg">Microsoft Entra Consent</h1>
      {renderContent()}
    </div>
  );
};

export default AdminConsentCallback;
